import type { Translations } from './index';

/**
 * Urdu strings. Typed as `Translations`, so this file fails to compile if a key is missing or
 * misspelled — a silent English fallback on a screen about someone's money is worse than a build
 * error.
 *
 * Financial terms follow the wording already used with account holders. Amounts themselves stay in
 * Latin digits with the PKR prefix: mixing Urdu digits into figures that also appear on receipts
 * and in the database makes the two harder to reconcile.
 */
export const ur: Translations = {
  common: {
    save: 'محفوظ کریں',
    cancel: 'منسوخ کریں',
    close: 'بند کریں',
    edit: 'ترمیم',
    delete: 'حذف کریں',
    add: 'شامل کریں',
    search: 'تلاش کریں',
    back: 'واپس',
    confirm: 'تصدیق کریں',
    upload: 'اپ لوڈ کریں',
    view: 'دیکھیں',
    download: 'ڈاؤن لوڈ',
    share: 'شیئر کریں',
    pdf: 'پی ڈی ایف',
    date: 'تاریخ',
    amount: 'رقم',
    notes: 'نوٹس',
    status: 'حیثیت',
    active: 'فعال',
    inactive: 'غیر فعال',
    closed: 'بند',
    type: 'قسم',
    none: '—',
    loading: 'لوڈ ہو رہا ہے…',
    noData: 'کوئی ڈیٹا نہیں',
    required: 'یہ خانہ ضروری ہے۔',
    language: 'زبان',
    english: 'English',
    urdu: 'اردو',
  },

  nav: {
    investmentDashboard: 'سرمایہ کاری ڈیش بورڈ',
    investors: 'سرمایہ کار',
    myInvestmentDashboard: 'میری سرمایہ کاری ڈیش بورڈ',
    myInvestments: 'میری سرمایہ کاری',
    myPortfolio: 'میرا پورٹ فولیو',
    reports: 'رپورٹس',
    calculator: 'کیلکولیٹر',
    users: 'صارفین',
    auditLog: 'آڈٹ لاگ',
    logOut: 'لاگ آؤٹ',
  },

  auth: {
    signIn: 'سائن ان',
    username: 'صارف نام',
    password: 'پاس ورڈ',
    usernameRequired: 'صارف نام ضروری ہے۔',
    passwordRequired: 'پاس ورڈ ضروری ہے۔',
    appName: 'انویسٹمنٹ سسٹم',
    sessionExpired: 'آپ کا سیشن ختم ہو گیا ہے۔ دوبارہ سائن ان کریں۔',
  },

  roles: {
    Admin: 'ایڈمن',
    Accountant: 'اکاؤنٹنٹ',
    Viewer: 'ناظر',
    Investor: 'سرمایہ کار',
    Dealer: 'ڈیلر',
  },

  tabs: {
    profile: 'پروفائل',
    investments: 'سرمایہ کاری',
    withdrawals: 'رقم کی واپسی',
    profit: 'منافع کی رقم',
    ledger: 'کھاتہ',
    receipts: 'رسیدیں',
    overview: 'خلاصہ',
  },

  party: {
    fullName: 'پورا نام',
    fatherName: 'والد کا نام',
    mobile: 'موبائل نمبر',
    cnic: 'شناختی کارڈ نمبر',
    address: 'پتہ',
    photo: 'تصویر',
    cnicImage: 'شناختی کارڈ کی تصویر',
    agreementImage: 'معاہدے کی تصویر',
    agreementVideo: 'معاہدے کی ویڈیو',
    memberSince: 'رکنیت کی تاریخ',
    description: 'تفصیل',
    reason: 'وجہ',
    recentTransactions: 'حالیہ لین دین',
  },

  finance: {
    totalInvestment: 'کل سرمایہ کاری',
    totalInvestmentRemaining: 'کل باقی سرمایہ کاری',
    investmentWithdrawn: 'نکالی گئی سرمایہ کاری',
    totalProfit: 'کل منافع',
    totalProfitRemaining: 'کل باقی منافع',
    percentageInvestment: 'فیصد سرمایہ کاری',
    percentageProfitRemaining: 'فیصد باقی منافع',
    fixedInvestment: 'طے شدہ سرمایہ کاری',
    fixedRemaining: 'طے شدہ باقی',
    totalPaidProfit: 'کل ادا شدہ منافع',
    remainingAmount: 'باقی رقم',
    remainingInvestment: 'باقی سرمایہ کاری',
    totalWithdrawn: 'کل نکالی گئی رقم',
    totalPayable: 'کل قابل ادائیگی رقم',
    returnAgreed: 'طے شدہ واپسی کی رقم',
    profitRate: 'ماہانہ منافع کی شرح',
    paymentMethod: 'ادائیگی کا طریقہ',
    accountNumber: 'اکاؤنٹ / والٹ نمبر',
    transactionProof: 'لین دین کا ثبوت',
    receiptNo: 'رسید نمبر',
    investment: 'سرمایہ کاری',
    withdrawal: 'رقم کی واپسی',
    profitPayment: 'منافع کی ادائیگی',
    amountPkr: 'رقم (روپے)',
    investmentDate: 'سرمایہ کاری کی تاریخ',
    withdrawalDate: 'رقم نکالنے کی تاریخ',
    paymentDate: 'ادائیگی کی تاریخ',
    monthlyProfitRate: 'ماہانہ منافع کی شرح (%)',
    withdrawn: 'نکالی گئی رقم',
    remaining: 'باقی',
    totalAmount: 'کل رقم',
    totalRemaining: 'کل باقی',
    payableRemaining: 'باقی قابل ادائیگی',
    totalPayableRemaining: 'کل باقی قابل ادائیگی',
    receipt: 'رسید',
    transactionProofOptional: 'لین دین کا ثبوت (اختیاری)',
    totalReturnAgreed: 'طے شدہ کل واپسی کی رقم',
  },

  actions: {
    addInvestment: 'سرمایہ کاری شامل کریں',
    withdrawFromInvestment: 'کل سرمایہ کاری سے رقم نکالیں',
    profitWithdrawn: 'منافع نکالا گیا',
    recordPayment: 'منافع کی ادائیگی درج کریں',
  },

  table: {
    name: 'نام',
    code: 'کوڈ',
    actions: 'کارروائیاں',
    role: 'کردار',
    created: 'تاریخِ اندراج',
    username: 'صارف نام',
    method: 'طریقہ',
    rate: 'شرح',
    from: 'از',
    to: 'تا',
    days: 'دن',
    debit: 'ڈیبٹ',
    credit: 'کریڈٹ',
    tranche: 'قسط',
    transactions: 'لین دین',
    nextDueDate: 'اگلی مقررہ تاریخ',
    asOf: 'بتاریخ',
  },

  forms: {
    father: 'والد',
    profilePhoto: 'پروفائل تصویر',
    investmentType: 'سرمایہ کاری کی قسم',
    fixedReturnAmount: 'طے شدہ واپسی کی رقم (روپے)',
    returnDueOn: 'واپسی کی مقررہ تاریخ',
    profitExpectedOn: 'منافع متوقع تاریخ',
    receiptNoAuto: 'رسید نمبر (خالی چھوڑنے پر خودکار)',
    newPasswordBlank: 'نیا پاس ورڈ (تبدیل نہ کرنے کے لیے خالی چھوڑیں)',
    addInvestor: 'سرمایہ کار شامل کریں',
    addMyInvestment: 'میری سرمایہ کاری شامل کریں',
    addStaffUser: 'عملے کا صارف شامل کریں',
    percentageHint: 'ماہانہ فیصد کی بنیاد پر — منافع روزانہ شرح سے بنتا ہے',
    percentageBook: 'ماہانہ فیصد کی بنیاد پر',
    fixedBook: 'طے شدہ رقم کی بنیاد پر',
    fixedInvestmentHint: 'طے شدہ سرمایہ کاری — مقررہ رقم واپس آتی ہے',
  },

  dashboard: {
    totalInvestors: 'کل سرمایہ کار',
    today: 'آج',
    paidThisMonth: 'اس ماہ ادا شدہ',
    investmentPlusDebt: 'سرمایہ کاری + باقی واجبات',
    fixedInvestorsIn: 'طے شدہ منافع — سرمایہ کار (آمدنی)',
    fixedDealersOut: 'طے شدہ منافع — میری سرمایہ کاری (اخراجات)',
    profitPaid12: 'ادا شدہ منافع — گزشتہ 12 ماہ',
    capitalInOut12: 'وصول شدہ اور واپس کی گئی رقم — گزشتہ 12 ماہ',
    firstInvestment: 'پہلی سرمایہ کاری',
    initialDeposit: 'ابتدائی جمع رقم',
    additionalDeposits: 'اضافی جمع رقوم',
    totalDeposited: 'کل جمع شدہ رقم',
    paymentHistory: 'ادائیگیوں کی تاریخ',
    profitTillNow: 'کل منافع (اب تک)',
    profitPeriod: 'کل منافع (مدت)',
    investmentPlusProfit: 'کل باقی سرمایہ کاری + کل منافع',
  },
  page: {
    accountAccess: 'اکاؤنٹ اور رسائی',
    personalInformation: 'ذاتی معلومات',
    editProfile: 'پروفائل میں ترمیم',
    reissuePassword: 'پاس ورڈ دوبارہ جاری کریں',
    sendAccountDetails: 'اکاؤنٹ کی تفصیلات بھیجیں',
    investorCode: 'سرمایہ کار کوڈ',
    myInvestmentCode: 'میری سرمایہ کاری کا کوڈ',
    loginUsername: 'لاگ ان صارف نام',
    lastUpdated: 'آخری تبدیلی',
    joined: 'شمولیت',
    documents: 'دستاویزات',
    agreement: 'معاہدہ',
  },
  reports: {
    investorStatement: 'سرمایہ کار کا گوشوارہ',
    profit: 'منافع کی رپورٹ',
    outstandingProfit: 'باقی منافع کی رپورٹ',
    daily: 'روزانہ رپورٹ',
    monthly: 'ماہانہ رپورٹ',
    yearly: 'سالانہ رپورٹ',
  },

  messages: {
    investmentAdded: 'سرمایہ کاری شامل کر دی گئی۔',
    withdrawalRecorded: 'رقم کی واپسی درج کر دی گئی۔',
    paymentRecorded: 'منافع کی ادائیگی درج کر دی گئی۔',
    proofUploaded: 'لین دین کا ثبوت اپ لوڈ ہو گیا۔',
    amountMustBePositive: 'رقم صفر سے زیادہ ہونی چاہیے۔',
    overAmount:
      'کارروائی ممکن نہیں — {{entered}} آپ کی {{label}} {{available}} سے زیادہ ہے۔ {{available}} یا اس سے کم رقم درج کریں۔',
  },
};
